import h from 'virtual-dom/h'
import * as Actions from '../actions/actions'
import * as CaptureTools from '../utilities/capture-tools'

export default class Renderer {
    constructor(settings, mutators, childRenderers) {
        this._settings = settings;
        this._mutators = mutators;
        this._childRenderers = childRenderers;

        try {
            this.setupSelectors(settings);
        } catch (e) {
            console.error(`nextitWindowFeedbackOverlay.RadioOptionPane error: ${e}`);
        }
    }

    setupSelectors(settings) {
        let windowRoot = settings.Window.rootId;
        let feedbackClass = settings.classNames.feedbackOverlay;
        let radioClass = settings.classNames.radioOptionsFeedbackPane;
        let textAreaClass = settings.classNames.feedbackOtherTextArea;
        let feedbackBodyClass = settings.classNames.feedbackBody;

        let baseQuery = `#${windowRoot} > div > div.${feedbackClass} > .${radioClass}[data-active="true"] > .${feedbackBodyClass} > `;
        this._selectedOptionQuery = this._settings.Analytics.Feedback.selectedOptionQuery ||
            baseQuery + `form > input[type="radio"]:checked`;
        this._freeformTextQuery = this._settings.Analytics.Feedback.freeformTextQuery ||
            baseQuery + `.${textAreaClass} > textarea`;
    }

    initialize(store) {
        this._store = store;
    }

    doesRender(message) {
        return message.type === this._settings.ResponseActions.displayFeedbackPanes.paneTypes.options;
    }

    getRender() {
        return (message) => {
            if(!this.doesRender(message)) return;

            let parameters = {
                className : this._settings.classNames.radioOptionsFeedbackPane
            };

            this._mutators.forEach((mutate) => {
                parameters = mutate(parameters);
            });

            let childNodes = [];

            this._childRenderers.forEach((render) => {
                childNodes.push(render(message));
            });

            let {
                closeIcon,
                header,
                prompt,
                paneId,
                active,
                metadata
            } = message;

            let attributes = {};
            attributes['data-id'] = paneId;
            attributes['data-active'] = active;

            parameters['attributes'] = attributes;

            let closeIconNode = this.buildCloseIcon(closeIcon, metadata, paneId);
            let headerNode = this.buildHeader(header);
            let bodyNodes = this.buildBody(prompt, metadata, paneId);

            childNodes = childNodes.concat(closeIconNode, headerNode, bodyNodes);

            return h('div', parameters, childNodes);
        }
    }

    buildHeader(headerText) {
        let parameters = {
            className: this._settings.classNames.feedbackHeader
        };

        return h('div', parameters, headerText);
    }

    buildBody(promptText, metadata, paneId) {

        let panesLocation = this._settings.stateLocations.History.feedbackPanes;
        let activeLocation = this._settings.stateLocations.History.displayFeedback;
        let captureLocation = this._settings.stateLocations.History.capturedFeedback;
        let capturedStageLocation = this._settings.stateLocations.History.capturedStageLocation;

        let promptElement = h('div', {
            className : this._settings.classNames.feedbackPrompt
        }, promptText);

        let optionsElements = [];
        let optionsLength = metadata.options.length;

        for(let i = 0; i < optionsLength; i++) {

            let tempOptionParams = {
                className : this._settings.classNames.feedbackRadioOption,
                type: "radio",
                name: "feedbackradio",
                id: "radioOption" + paneId + metadata.options[i].analyticsValue,
                value: metadata.options[i].analyticsValue,
                onclick: (e) => {
                    let elm = e.target;
                    let isOther = elm.getAttribute("data-other");

                    if(elm.parentElement) {
                        elm.parentElement.setAttribute("data-other", isOther);
                    }
                },
                oninput: () => {if (metadata.enableSubmitButtonOnValidation) this.enableSubmitButton()}
            };

            if(i+1 === optionsLength && metadata.textArea.toLowerCase() === "true") {
               tempOptionParams["attributes"] = {};
               tempOptionParams["attributes"]["data-other"] = "true";
            }

            let tempOption = h('input', tempOptionParams);

            let tempLabel = h('label', {
                    attributes: {
                        "for": "radioOption" + paneId + metadata.options[i].analyticsValue
                    }
                }, metadata.options[i].displayText);


            optionsElements.push(tempOption);
            optionsElements.push(tempLabel);
            optionsElements.push(h('br'));
        }

        let textElement = h('div', {
            className : this._settings.classNames.feedbackOtherTextArea
        });

        if(metadata.textArea.toLowerCase() === "true") {
            textElement = h('div', {
                className : this._settings.classNames.feedbackOtherTextArea
            }, [
                h('textarea')
            ]);
        }

        let buttonElement = h('div', {
            className : this._settings.classNames.radioOptionFeedbackButtonArea
        }, [
            h('button', {
                className : this._settings.classNames.radioOptionFeedbackButton,
                disabled: !!metadata.enableSubmitButtonOnValidation,
                onclick : () => {
                    const selectedOption = document.querySelector(this._selectedOptionQuery),
                        isOther = selectedOption.getAttribute("data-other");
                    let feedbackReason;

                    if (isOther) {
                        feedbackReason = document.querySelector(this._freeformTextQuery).value;
                    } else {
                        feedbackReason = selectedOption.getAttribute("value");
                    }

                    CaptureTools.updateFeedback(this._store, captureLocation, paneId, {
                         category : this._settings.Analytics.Feedback.category,
                         action : this._settings.Analytics.Feedback.actions.completePane,
                         label: feedbackReason,
                         value : paneId
                    });
                    if(metadata.nextPane === this._settings.ResponseActions.displayFeedbackPanes.nextPaneEndKey) {
                        CaptureTools.endFeedback(this._store, captureLocation, capturedStageLocation, paneId, {
                            category : this._settings.Analytics.Feedback.category,
                            action : this._settings.Analytics.Feedback.actions.completeFeedback
                        });
                        this._store.dispatch(Actions.endDisplayFeedbackPanes(panesLocation, activeLocation));
                    } else {
                        this._store.dispatch(Actions.setActiveFeedbackPane(metadata.nextPane, panesLocation))
                    }
                }
            }, metadata.buttonText)
        ]);

        return h('div', {
            className : this._settings.classNames.feedbackBody
        }, [
            promptElement,
            h('form', {
                className : this._settings.classNames.radioOptionsForm
            }, [optionsElements]),
            textElement,
            buttonElement
        ]);
    }

    buildCloseIcon(closeIcon, metadata, paneId)
    {
        let properties =
        {
            className: this._settings.classNames.dataCollectionCloseButton,
            onclick: () => { this.cancelPane(metadata, paneId); }
        };

        return closeIcon ? h('button', properties) : [];
    }

    enableSubmitButton() {
        let button = document.querySelector(`.${this._settings.classNames.radioOptionFeedbackButton}`);
        if (button) {
            button.removeAttribute("disabled");
        }
    }

    cancelPane(metadata, paneId) {
        let panesLocation = this._settings.stateLocations.History.feedbackPanes;
        let activeLocation = this._settings.stateLocations.History.displayFeedback;
        let capturedLocation = this._settings.stateLocations.History.capturedFeedback;
        let capturedStageLocation = this._settings.stateLocations.History.capturedStageLocation;

        // abort the pane when user cancels
        CaptureTools.updateFeedback(this._store, capturedLocation, paneId, {
            category: this._settings.Analytics.Feedback.category,
            action: this._settings.Analytics.Feedback.actions.abortPane,
            label: {},
            value: paneId
        });

        // continue to the next pane or end
        if (metadata.nextPane === this._settings.ResponseActions.displayFeedbackPanes.nextPaneEndKey) {
            CaptureTools.endFeedback(this._store, capturedLocation, capturedStageLocation, paneId, {
                category: this._settings.Analytics.Feedback.category,
                action: this._settings.Analytics.Feedback.actions.abortFeedback
            });
            this._store.dispatch(Actions.endDisplayFeedbackPanes(panesLocation, activeLocation));
        } else {
            this._store.dispatch(Actions.setActiveFeedbackPane(metadata.nextPane, panesLocation));
        }
    }
}




// WEBPACK FOOTER //
// ./node_modules/@alme/ui-web/modules/window-feedback-overlay/src/renderers/radioOptionPane.js